import type { ReactNode } from "react"

import { cx } from "../lib/cx"
import { Skeleton } from "../shell/states"
import styles from "./journal-states.module.css"

/**
 * Состояния сетки дня.
 *
 * ТРИ ПРАВИЛА, КОТОРЫЕ ЗДЕСЬ ВИДНО:
 * 1. Каркас повторяет ФОРМУ сетки, а не заменяет её спиннером: по расположению колонок
 *    администратор начинает ориентироваться раньше, чем приедут данные (ТЗ §2.11.1).
 * 2. Пустота бывает четырёх видов, и это четыре разных ответа. «Нет мастеров» и «записей
 *    нет» — не одно и то же: в первом случае салон не настроен, во втором он работает.
 * 3. Устаревшие данные не прячутся. Возраст показывается всегда, а не когда он велик:
 *    подпись времени — единственное, что отделяет рабочий экран от вранья (§2.11.4).
 */

/** Подписи шкалы в каркасе. Из макета: пустая строка сверху, затем часы рабочего дня. */
const SKELETON_HOURS = ["", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00"]
/** Ширины плашек в шапке колонок — разные, чтобы каркас не выглядел напечатанным. */
const SKELETON_NAME_WIDTHS = [110, 140, 90, 120, 130, 100]
const SKELETON_ROLE_WIDTHS = [60, 70, 50, 80, 60, 70]
/** Положения блоков-записей: top от начала тела, height. */
const SKELETON_BLOCKS: readonly (readonly [number, number])[] = [
  [104, 46],
  [174, 78],
  [284, 46],
  [374, 104],
  [504, 60],
]

export function JournalSkeleton(): ReactNode {
  return (
    <div className={styles["skeleton"]} aria-busy="true" aria-label="Расписание загружается">
      <div className={styles["skeletonScale"]}>
        {SKELETON_HOURS.map((hour, index) => (
          <div key={`${hour}-${String(index)}`} className={styles["skeletonRow"]}>
            {hour}
          </div>
        ))}
      </div>
      <div className={styles["skeletonColumns"]}>
        {SKELETON_NAME_WIDTHS.map((nameWidth, column) => (
          <div key={nameWidth} className={styles["skeletonColumn"]}>
            <div className={styles["skeletonHeader"]}>
              <div className={cx(styles["skeletonAvatar"], "plnr-skeleton")} />
              <div className={styles["skeletonHeaderText"]}>
                <Skeleton width={nameWidth} height={10} radius={3} />
                <Skeleton width={SKELETON_ROLE_WIDTHS[column] ?? 60} height={8} radius={4} />
              </div>
            </div>
            {SKELETON_BLOCKS.map(([top, height], row) =>
              // Пропуск по остатку от деления: без него колонки каркаса совпадают
              // строка в строку и вместо расписания получается штрихкод.
              (column + row) % 3 === 1 ? null : (
                <div
                  key={top}
                  className={cx(styles["skeletonBlock"], "plnr-skeleton")}
                  style={{ top: `${top}px`, height: `${height}px` }}
                />
              ),
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export type JournalEmptyKind = "no-employees" | "no-shifts" | "closed"

/**
 * Пустой день.
 *
 * Случая «всё настроено, записей нет» здесь НЕТ намеренно: он не пустое состояние,
 * а работающая сетка с подсказкой поверх. Салон обязан увидеть свой день, а не заглушку.
 */
export function JournalEmpty(props: {
  readonly kind: JournalEmptyKind
  readonly canManage: boolean
  readonly reason?: string | null
}): ReactNode {
  const content = describe(props.kind, props.reason ?? null)
  return (
    <div className={styles["centered"]}>
      <div className={cx(styles["empty"], props.canManage ? undefined : styles["emptyQuiet"])}>
        <p className={styles["emptyTitle"]}>{content.title}</p>
        <p className={styles["emptyText"]}>{content.text}</p>
        {/* Кнопка появляется только у того, кто может это исправить: администратору без
            прав она обещает действие, которого у него нет. */}
        {props.canManage && content.action !== null ? (
          <div className={styles["emptyActions"]}>
            <button type="button" className={styles["primary"]}>
              {content.action}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function describe(
  kind: JournalEmptyKind,
  reason: string | null,
): { readonly title: string; readonly text: string; readonly action: string | null } {
  switch (kind) {
    case "no-employees":
      return {
        title: "Чтобы вести запись, добавьте хотя бы одного мастера",
        text: "Записи появятся в журнале, как только у мастера будут услуги и смена. Первые три шага занимают 10 минут.",
        action: "Добавить мастера",
      }
    case "no-shifts":
      return {
        title: "Сегодня ни у кого нет смены",
        text: "Настройте график работы — или поставьте рабочий день вручную прямо на эту дату, если мастер вышел подменить.",
        action: "Настроить график",
      }
    case "closed":
      return {
        title: "Сегодня филиал закрыт",
        text:
          reason === null
            ? "Это выходной по графику филиала. Записи на этот день не принимаются — ни в кабинете, ни онлайн."
            : `${reason}. Записи на этот день не принимаются — ни в кабинете, ни онлайн.`,
        action: null,
      }
  }
}

/**
 * Полоса «данные устарели».
 *
 * Показывается поверх УЖЕ ЗАГРУЖЕННОГО дня, а не вместо него: чтение расписания работает
 * всегда, изменения — только со связью (GAPS D4). Поэтому день на экране остаётся,
 * а полоса честно подписывает его возраст.
 */
export function StaleDataStripe(props: {
  readonly generatedAtLabel: string
  readonly onRetry: () => void
}): ReactNode {
  return (
    <div className={styles["stale"]} role="status">
      <span className={styles["staleTitle"]}>Нет связи.</span>
      <span className={styles["staleText"]}>
        Показан день на {props.generatedAtLabel}, изменения сейчас не сохраняются
      </span>
      <button type="button" className={styles["staleAction"]} onClick={props.onRetry}>
        Повторить
      </button>
    </div>
  )
}
